/**
 * SequenceDiagram v2 (spec §6 task #D2).
 *
 * 4-lane SVG: USER · EVENT-BUS · CF-TRIGGER · CF-CALLABLE
 * Renders activation bars, directed arrows, safety tier chips,
 * and latency annotations. No external diagram libs.
 *
 * Backward-compat: also accepts unenriched SimulationTrace (renders
 * without enrichments — no chips, no latency badges).
 */
import type { SimulationTrace, EnrichedSimulationTrace, EnrichedSimulationStep } from '../state/simulator.js';

export interface SequenceDiagramProps {
  trace: SimulationTrace | EnrichedSimulationTrace;
}

const ROW_H = 72;
const BAR_W = 12;
const W = 840;
const MARGIN_RIGHT = 60;

const LANE_X: Record<string, number> = {
  user: 90,
  'event-bus': 290,
  cf: 490,
  'cf-callable': 690,
};

const LANE_LABELS: Record<string, string> = {
  user: 'USER',
  'event-bus': 'EVENT-BUS',
  cf: 'CF-TRIGGER',
  'cf-callable': 'CF-CALLABLE',
};

const TIER_COLORS: Record<string, string> = {
  BLOCK: '#ef4444',
  WARN: '#f59e0b',
  ALLOW: '#22c55e',
};

function isEnriched(trace: SimulationTrace | EnrichedSimulationTrace): trace is EnrichedSimulationTrace {
  return 'totalLatencyMs' in trace;
}

function isEnrichedStep(step: unknown): step is EnrichedSimulationStep {
  return typeof step === 'object' && step !== null && 'cumulativeLatencyMs' in step;
}

function ArrowMarkers(): React.JSX.Element {
  return (
    <defs>
      <marker id="arrow-fwd" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto">
        <path d="M0,0 L0,6 L8,3 z" fill="var(--text3)" />
      </marker>
      <marker id="arrow-block" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto">
        <path d="M0,0 L0,6 L8,3 z" fill="#ef4444" />
      </marker>
      <marker id="arrow-warn" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto">
        <path d="M0,0 L0,6 L8,3 z" fill="#f59e0b" />
      </marker>
    </defs>
  );
}

interface StepRowProps {
  step: SimulationTrace['steps'][number] | EnrichedSimulationStep;
  prevActorX?: number;
  y: number;
}

function StepRow({ step, prevActorX, y }: StepRowProps): React.JSX.Element {
  const enriched = isEnrichedStep(step) ? step : null;
  const actorX = LANE_X[step.actor] ?? LANE_X.cf;
  const tier = enriched?.agentGate?.tier;
  const markerRef = tier === 'BLOCK' ? 'url(#arrow-block)' : tier === 'WARN' ? 'url(#arrow-warn)' : 'url(#arrow-fwd)';
  const arrowColor = tier ? TIER_COLORS[tier] : 'var(--text3)';

  return (
    <g
      className={`seq-step seq-actor-${step.actor}${enriched?.fromSandboxOverlay ? ' highlight' : ''}`}
      data-testid={`sequence-step-${step.hop}`}
    >
      {/* Activation bar */}
      <rect
        x={actorX - BAR_W / 2}
        y={y - ROW_H / 2 + 4}
        width={BAR_W}
        height={ROW_H - 8}
        rx={2}
        className="seq-activation"
        fill={tier ? TIER_COLORS[tier] : 'var(--surface2)'}
        opacity={0.7}
      />

      {/* Arrow from previous actor */}
      {prevActorX !== undefined && prevActorX !== actorX && (
        <line
          x1={prevActorX + (prevActorX < actorX ? BAR_W / 2 : -BAR_W / 2)}
          y1={y}
          x2={actorX + (prevActorX < actorX ? -BAR_W / 2 - 6 : BAR_W / 2 + 6)}
          y2={y}
          stroke={arrowColor}
          strokeWidth={1.5}
          markerEnd={markerRef}
        />
      )}

      {/* Source label */}
      <text
        x={actorX + BAR_W}
        y={y - 6}
        className="seq-source"
        fontSize={11}
        fill="var(--text1)"
      >
        {step.source.length > 28 ? `${step.source.slice(0, 26)}…` : step.source}
      </text>

      {/* Writes */}
      {step.writes.length > 0 && (
        <text x={actorX + BAR_W} y={y + 10} className="seq-writes" fontSize={10} fill="var(--text3)">
          writes: {step.writes.slice(0, 3).join(', ')}{step.writes.length > 3 ? '…' : ''}
        </text>
      )}

      {/* Events fired */}
      {step.eventsFired.length > 0 && (
        <text x={actorX + BAR_W} y={y + 24} className="seq-events" fontSize={9} fill="var(--text3)" opacity={0.8}>
          fires: {step.eventsFired.slice(0, 2).join(', ')}{step.eventsFired.length > 2 ? '…' : ''}
        </text>
      )}

      {/* Sandbox overlay annotation */}
      {enriched?.fromSandboxOverlay && (
        <text x={actorX + BAR_W} y={y + 32} className="seq-overlay-note" fontSize={9} fill="var(--caution)">
          from sandbox overlay
        </text>
      )}

      {/* Safety tier chip */}
      {tier && tier !== 'ALLOW' && (
        <g>
          <rect
            x={W - MARGIN_RIGHT - 52}
            y={y - 10}
            width={50}
            height={18}
            rx={4}
            fill={TIER_COLORS[tier]}
            opacity={0.9}
          />
          <text
            x={W - MARGIN_RIGHT - 27}
            y={y + 3}
            textAnchor="middle"
            fontSize={9}
            fill="#fff"
            fontWeight="700"
          >
            {tier}
          </text>
        </g>
      )}

      {/* Latency badge */}
      {enriched?.latency && (
        <text
          x={W - MARGIN_RIGHT + 4}
          y={y + 4}
          fontSize={9}
          fill="var(--text3)"
          className="seq-latency mono"
        >
          {enriched.latency.p95}ms
        </text>
      )}

      {/* Cumulative latency */}
      {enriched && (
        <text
          x={W - MARGIN_RIGHT + 4}
          y={y + 16}
          fontSize={8}
          fill="var(--text3)"
          opacity={0.6}
          className="seq-cumulative mono"
        >
          Σ{enriched.cumulativeLatencyMs}ms
        </text>
      )}

      {/* Dashed return message (spec §4) — sits at y+40/y+36 to clear writes (y+10) and eventsFired (y+24) text */}
      {step.writes.length > 0 && step.actor !== 'user' && prevActorX !== undefined && prevActorX !== actorX && (
        <g className="seq-return-msg">
          <line
            x1={actorX + (prevActorX < actorX ? -BAR_W / 2 - 4 : BAR_W / 2 + 4)}
            y1={y + 40}
            x2={prevActorX + (prevActorX < actorX ? BAR_W / 2 + 4 : -BAR_W / 2 - 4)}
            y2={y + 40}
            stroke="var(--text3)"
            strokeWidth={1}
            strokeDasharray="4 3"
            markerEnd="url(#arrow-fwd)"
            opacity={0.5}
          />
          <text
            x={(actorX + prevActorX) / 2}
            y={y + 36}
            textAnchor="middle"
            fontSize={8}
            fill="var(--text3)"
            opacity={0.5}
          >
            return: ok
          </text>
        </g>
      )}
    </g>
  );
}

export function SequenceDiagram({ trace }: SequenceDiagramProps): React.JSX.Element {
  if (trace.steps.length === 0) {
    return (
      <div className="sequence-diagram-empty" aria-label="Empty sequence diagram">
        <p>No steps to render — this action has no downstream wiring in the manifest.</p>
      </div>
    );
  }

  const enriched = isEnriched(trace);
  const H = trace.steps.length * ROW_H + 80;

  return (
    <div className="sequence-diagram">
      <svg
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="xMinYMin meet"
        aria-label={`Sequence diagram for: ${trace.action.label}`}
        style={{ width: '100%', height: H }}
      >
        <ArrowMarkers />

        {/* Lane header row */}
        {Object.entries(LANE_X).map(([actor, x]) => (
          <g key={actor}>
            <text x={x} y={22} textAnchor="middle" className="seq-lane" fontSize={11} fill="var(--text2)" fontWeight="600">
              {LANE_LABELS[actor]}
            </text>
            <line x1={x} y1={30} x2={x} y2={H - 20} className="seq-lane-line" stroke="var(--surface3)" strokeWidth={1} strokeDasharray="4 4" />
          </g>
        ))}

        {/* Step rows */}
        {trace.steps.map((step, i) => {
          const y = 50 + i * ROW_H + ROW_H / 2;
          const prevStep = i > 0 ? trace.steps[i - 1] : null;
          const prevActorX = prevStep ? (LANE_X[prevStep.actor] ?? LANE_X.cf) : undefined;
          return (
            <StepRow
              key={step.hop}
              step={step}
              prevActorX={prevActorX}
              y={y}
            />
          );
        })}

        {/* Total latency footer */}
        {enriched && (
          <text
            x={W / 2}
            y={H - 6}
            textAnchor="middle"
            fontSize={10}
            fill="var(--text3)"
            className="seq-total mono"
          >
            Total p95: {(trace as EnrichedSimulationTrace).totalLatencyMs} ms · {trace.steps.length} steps
          </text>
        )}
      </svg>
    </div>
  );
}
